import { faChevronsRight, faRobot, faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Autocomplete, TextField } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { ListAltOutlined, Person2Outlined } from "@mui/icons-material";

import { Button } from "@/components/Button/Button";
import { Icon, PermissionCheck } from "@/components";
import { ERROR_TYPE, translateError } from "@/utils";
import { useGetAllTagsQuery } from "@/pages/Private/redux/to-do/to-do.api";
import DeleteModal from "@/components/Modals/DeleteModal";
import { ButtonColor } from "@/components/Button/types";
import { PermissionRoles } from "@/enum";

import { useGetAssigneesQuery } from "@/pages/Private/redux/tasks-and-subtasks/tasks-and-subtasks.api";
import { sharedSx, sharedInputProps, sharedMenuProps } from "@/styles/sharedComponentStyles";

import {
	SubTaskInfo,
	TaskInfo,
	UpdateSubTaskInfo,
	UpdateSubTaskInfoSchema,
} from "../schema/tasks-and-subtasks";
import { ToDoStatusDropDown } from "../../ToDo/ToDoList/components/ToDoStatusDropdown";
import Activity from "./Activity";
import { useToDoSocket } from "../../ToDo/ToDoList/providers/SocketContext";

const AiChat = lazy(() => import("./AiChat"));

interface SubTaskDetailsProps {
	subTask: SubTaskInfo | null;
	parentTaskId: number;
	open: boolean;
	onClose: () => void;
	parentData: TaskInfo;
}

export default function SubTaskDetails({
	subTask,
	parentTaskId,
	open,
	onClose,
}: SubTaskDetailsProps) {
	const { t } = useTranslation();

	const subTaskRef = useRef(subTask);
	const { updateSubTask, deleteSubTask } = useToDoSocket();

	useEffect(() => {
		subTaskRef.current = subTask;
	}, [subTask]);

	const { data: users } = useGetAssigneesQuery();

	const { data: tags } = useGetAllTagsQuery();

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<UpdateSubTaskInfo>({
		defaultValues: {
			id: subTask?.id ?? -1,
			taskId: parentTaskId,
			title: subTask?.title ?? "",
			description: subTask?.description ?? "",
			complexityEstimate: subTask?.complexityEstimate,
			status: subTask?.status,
			priority: subTask?.priority,
			tagId: subTask?.tagId ?? null,
			assigneeId: subTask?.assigneeId,
		},
		resolver: zodResolver(UpdateSubTaskInfoSchema),
	});

	useEffect(() => {
		if (subTask) {
			reset({
				id: subTask.id,
				taskId: parentTaskId,
				title: subTask.title,
				description: subTask.description ?? "",
				complexityEstimate: subTask.complexityEstimate,
				status: subTask.status,
				priority: subTask.priority,
				tagId: subTask.tagId ?? null,
				assigneeId: subTask.assigneeId,
			});
		}
	}, [
		subTask?.id,
		subTask?.assigneeId,
		subTask?.title,
		subTask?.status,
		subTask?.priority,
		subTask?.tagId,
	]);

	const unassignedOption = {
		firstName: t("createTask.unassigned"),
		lastName: "",
		assigneeId: null as unknown as number,
	};

	const assigneeOptions = [
		unassignedOption,
		...(users?.map((user) => ({
			firstName: user.firstName,
			lastName: user.lastName,
			assigneeId: +user.id,
		})) ?? []),
	].filter((o) => o?.firstName || o?.lastName);

	const tagOptions: { tagId: number; tagName: string }[] =
		tags
			?.filter((t): t is { id: number; name: string } => !!t.id)
			.map((t) => ({ tagId: t.id, tagName: t.name })) ?? [];

	const onSubmit = useCallback(
		(formValues: UpdateSubTaskInfo) => {
			const current = subTaskRef.current;

			if (!current) {
				return;
			}

			const merged: UpdateSubTaskInfo = {
				...formValues,
				id: current.id ?? -1,
				taskId: parentTaskId,
				status: current.status ?? formValues.status,
				description: formValues.description || null,
				tagId: formValues.tagId ?? null,
			};

			updateSubTask(merged.taskId, merged.id ?? -1, merged);
			reset(merged);
		},
		[updateSubTask, reset, parentTaskId]
	);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isAiChatOpen, setIsAiChatOpen] = useState(false);

	const handleClose = useCallback(async () => {
		onClose();
	}, [handleSubmit, onSubmit, onClose]);

	const handleSave = useCallback(async () => {
		await handleSubmit(onSubmit)();
	}, [handleSubmit, onSubmit]);

	useEffect(() => {
		if (!open || !subTask?.id) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key !== "Escape") return;
			if (e.defaultPrevented) return;
			if (isDeleteModalOpen || isAiChatOpen) return;
			handleClose();
		};
		document.addEventListener("keydown", onKey);

		return () => document.removeEventListener("keydown", onKey);
	}, [open, subTask?.id, isDeleteModalOpen, isAiChatOpen, handleClose]);

	return (
		<>
			<div
				aria-hidden
				className={`fixed inset-0 z-[55] bg-black/30 transition-opacity duration-300 ease-in-out
					${open && subTask?.id ? "opacity-100" : "opacity-0 pointer-events-none"}`}
				onClick={handleClose}
			/>
			<div
				className={`fixed right-0 top-0 z-[60] h-[100vh] w-[40vw] bg-white shadow-lg overflow-y-auto
					transition-all duration-300 ease-in-out
					${
						open && subTask?.id
							? "translate-x-0 opacity-100"
							: "translate-x-full opacity-0 pointer-events-none"
					}`}
			>
				{!!subTask?.id && (
					<>
						<div className="w-full p-[26px] border-b border-[#E6E6EC] flex items-center justify-between">
							<Button
								className="rounded-[12px] bg-white !min-w-[44px] !min-h-[44px] !max-w-[44px] !max-h-[44px] !p-0 !shadow-none hover:bg-primary-gray-lightest"
								image={<Icon className="text-primary-blue-dark w-5 h-5" icon={faChevronsRight} />}
								title=""
								onClick={handleClose}
							/>
						</div>

						<div className="p-6 w-full space-y-4  flex flex-col">
							<div className="w-full flex flex-col gap-2">
								<p className="text-[16px] font-bold leading-5 text-primary-blue-dark">
									{t("createTask.status")}
								</p>
								<ToDoStatusDropDown
									className="!m-0 w-fit"
									companyToDoProgressId={subTask.id}
									isTask={subTask?.isTask}
								/>
							</div>
							<div className="w-full flex flex-col gap-2">
								<p className="text-[16px] font-bold leading-5 text-primary-blue-dark">
									{t("createTask.title")}
								</p>
								<Controller
									control={control}
									name="title"
									render={({ field }) => (
										<TextField
											{...field}
											fullWidth
											error={!!errors?.title}
											helperText={
												!!errors?.title?.message
													? translateError[errors?.title?.message as unknown as ERROR_TYPE]
													: errors?.title?.message
											}
											inputProps={{ style: { padding: "8px" } }}
											size="small"
										/>
									)}
								/>
							</div>

							<div className="w-full flex flex-col gap-2">
								<p className="text-[16px] font-bold leading-5 text-primary-blue-dark">
									{t("createTask.description")}
								</p>
								<Controller
									control={control}
									name="description"
									render={({ field }) => (
										<TextField
											{...field}
											fullWidth
											multiline
											maxRows={6}
											minRows={6}
											size="small"
										/>
									)}
								/>
							</div>

							<div className="grid grid-cols-2 gap-4 items-start">
								<div className="w-full flex flex-col gap-2">
									<p className="text-[16px] font-bold leading-5 text-primary-blue-dark">
										{t("createTask.tags")}
									</p>
									<Controller
										control={control}
										name="tagId"
										render={({ field }) => (
											<Autocomplete<{ tagId: number; tagName: string }>
												getOptionLabel={(o) => o.tagName}
												isOptionEqualToValue={(a, b) => a.tagId === b.tagId}
												options={tagOptions}
												renderInput={(params) => (
													<TextField
														placeholder={t("to-do.none")}
														{...params}
														InputProps={{
															...params.InputProps,
															startAdornment: (
																<>
																	<ListAltOutlined
																		sx={{ color: "action.active", fontSize: 18, mr: 0.5 }}
																	/>
																	{params.InputProps.startAdornment}
																</>
															),
														}}
														inputProps={{ ...params.inputProps, ...sharedInputProps }}
														sx={sharedSx}
													/>
												)}
												size="small"
												slotProps={{ paper: { sx: sharedMenuProps.PaperProps.sx } }}
												value={tagOptions.find((o) => o.tagId === field.value) ?? null}
												onChange={(_, selected) => field.onChange(selected?.tagId ?? null)}
											/>
										)}
									/>
								</div>

								{/* <div className="w-full flex flex-col gap-2">
									<p className="text-[16px] font-bold leading-5 text-primary-blue-dark">
										{t("createTask.priority")}
									</p>
									<ToDoPriorityDropdown
										isSubtask={subTask.isTask}
										companyToDoProgressId={subTask.id}
									/>
								</div>

								<div className="w-full flex flex-col gap-2">
									<p className="text-[16px] font-bold leading-5 text-primary-blue-dark">
										{t("createTask.complexity")}
									</p>
									<Controller
										control={control}
										name="complexityEstimate"
										render={({ field }) => (
											<TextField
												fullWidth
												select
												SelectProps={{ MenuProps: sharedMenuProps }}
												inputProps={sharedInputProps}
												size="small"
												sx={sharedSx}
												value={field.value}
												onChange={(e) => field.onChange(Number(e.target.value))}
											>
												<MenuItem value={1}>{t("createTask.easy")}</MenuItem>
												<MenuItem value={2}>{t("createTask.normal")}</MenuItem>
												<MenuItem value={3}>{t("createTask.hard")}</MenuItem>
												<MenuItem value={4}>{t("createTask.very-hard")}</MenuItem>
												<MenuItem value={5}>{t("createTask.expert")}</MenuItem>
											</TextField>
										)}
									/>
								</div> */}
								<div className="w-full flex flex-col gap-2">
									<p className="text-[16px] font-bold leading-5 text-primary-blue-dark">
										{t("createTask.assignee")}
									</p>
									<Controller
										control={control}
										name="assigneeId"
										render={({ field }) => (
											<Autocomplete
												disableClearable
												className="w-full"
												defaultValue={
													assigneeOptions.find((o) => o?.assigneeId === subTask?.assigneeId) ??
													unassignedOption
												}
												getOptionLabel={(o) => `${o?.firstName ?? ""} ${o?.lastName ?? ""}`.trim()}
												isOptionEqualToValue={(a, b) => a?.assigneeId === b?.assigneeId}
												options={assigneeOptions}
												renderInput={(params) => (
													<TextField
														{...params}
														InputProps={{
															...params.InputProps,
															startAdornment: (
																<>
																	<Person2Outlined
																		sx={{ color: "action.active", fontSize: 18, mr: 0.5 }}
																	/>
																	{params.InputProps.startAdornment}
																</>
															),
														}}
														inputProps={{ ...params.inputProps, ...sharedInputProps }}
														sx={sharedSx}
													/>
												)}
												size="small"
												slotProps={{ paper: { sx: sharedMenuProps.PaperProps.sx } }}
												value={
													assigneeOptions.find((o) => o?.assigneeId === field.value) ??
													unassignedOption
												}
												onChange={(_, v) => field.onChange(v?.assigneeId ?? null)}
											/>
										)}
									/>
								</div>
							</div>

							<div className="w-full  flex justify-between">
								<Button
									className="rounded-[12px] !min-w-[44px] text-primary-blue-dark !min-h-[44px] !max-w-fit !py-2  !px-4 my-6 !border-[0.28px] !border-[#E6E6EC] hover:bg-primary-gray-lightest"
									color={ButtonColor.ACTION}
									title={t("createTask.save")}
									onClick={() => {
										if (subTask?.id) {
											handleSave();
										}
									}}
								/>
								<PermissionCheck requiredPermissions={[PermissionRoles.ADMIN]}>
									<Button
										className="rounded-[12px] bg-white !min-w-[44px] text-primary-blue-dark !min-h-[44px] !max-w-fit !py-2  !px-4 my-6 !border-[0.28px] !border-[#E6E6EC] hover:bg-primary-gray-lightest"
										color={ButtonColor.ACTION_SECONDARY}
										image={
											<Icon className="text-primary-blue-dark w-5 h-5 mr-1" icon={faTrashCan} />
										}
										title={t("createTask.deleteSubtask")}
										onClick={() => {
											if (subTask?.id) {
												setIsDeleteModalOpen(true);
											}
										}}
									/>
								</PermissionCheck>
							</div>
						</div>
						<div className="w-full px-6 pt-2 pb-1">
							<Button
								className="rounded-[12px] bg-white !min-w-fit !min-h-[44px] !max-w-fit !py-2 !px-4 !border-[0.28px] !border-[#E6E6EC] hover:bg-primary-gray-lightest"
								color={ButtonColor.ACTION_SECONDARY}
								image={<Icon className="text-primary-blue-hover w-5 h-5 mr-2" icon={faRobot} />}
								title={t("aiChat.toggle", "AI Assistant")}
								onClick={() => setIsAiChatOpen((v) => !v)}
							/>
						</div>

						<Suspense fallback={null}>
							<AiChat
								isOpen={isAiChatOpen}
								subtaskId={subTask.id}
								taskId={parentTaskId}
								onClose={() => setIsAiChatOpen(false)}
							/>
						</Suspense>

						<div className="w-full p-6 !pb-3">
							<Activity companyToDoProgressId={subTask?.id} isTask={subTask.isTask} />
						</div>
					</>
				)}
			</div>

			<DeleteModal
				description={t("manage-to-dos.delete-description", { taskTitle: subTask?.title })}
				handleClose={() => setIsDeleteModalOpen(false)}
				handleDelete={() => {
					if (subTask?.id) {
						deleteSubTask(parentTaskId, subTask.id);
						setIsDeleteModalOpen(false);
						onClose();
					}
				}}
				isShowing={isDeleteModalOpen}
				title={t("manage-to-dos.delete-title")}
			/>
		</>
	);
}
